import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { useEsc } from '../componentes/sobreposicao';
import { criacaoDaChave, criacoesDoSubsistema, type ContextoCriacao, type CriacaoNoModulo } from './modulos-web';
import { useSessao } from './sessao';

/**
 * Ajustes do CRM 27/10 (C6, K2, C8): as criações que os módulos publicam (`criacoes` do ModuloWeb) e o contexto que a página
 * aberta oferece para pré-preencher (a empresa ou o contato da ficha, o funil escolhido). Uma página publica o contexto com
 * `useContextoDaCriacao` e ele some quando ela sai; o botão flutuante e o menu "Criar" leem o mesmo contexto.
 */

let contextoAtual: ContextoCriacao = {};
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());

/** A página publica o que sabe enquanto estiver montada (objeto estável por valor: só os ids e nomes contam). */
export function useContextoDaCriacao(ctx: ContextoCriacao) {
  const chave = JSON.stringify(ctx);
  useEffect(() => {
    contextoAtual = JSON.parse(chave) as ContextoCriacao;
    avisar();
    return () => {
      contextoAtual = {};
      avisar();
    };
  }, [chave]);
}

export function useContextoCriacao(): ContextoCriacao {
  return useSyncExternalStore(
    (f) => {
      ouvintes.add(f);
      return () => ouvintes.delete(f);
    },
    () => contextoAtual,
  );
}

/** O estado de uma criação aberta: o diálogo do módulo, ou a navegação para o destino. */
export function useAbrirCriacao() {
  const navegar = useNavigate();
  const [aberta, setAberta] = useState<CriacaoNoModulo | null>(null);
  const abrir = (c: CriacaoNoModulo) => {
    if (c.criacao.componente) setAberta(c);
    else if (c.criacao.destino) navegar(c.criacao.destino);
  };
  return { aberta, abrir, fechar: () => setAberta(null) };
}

/** Diálogo da criação aberta (o componente do módulo), com o contexto da página. */
export function DialogoCriacao({ aberta, aoFechar, contexto, aoCriar }: { aberta: CriacaoNoModulo | null; aoFechar: () => void; contexto?: ContextoCriacao; aoCriar?: (r: { id: string; nome?: string }) => void }) {
  const doContexto = useContextoCriacao();
  if (!aberta?.criacao.componente) return null;
  const C = aberta.criacao.componente;
  return <C aoFechar={aoFechar} contexto={contexto ?? doContexto} aoCriar={aoCriar} />;
}

/** A criação de uma chave (empresa, contato) para usar dentro de outro formulário. */
export function useCriacaoDaChave(chave: string) {
  const s = useSessao();
  return criacaoDaChave(s, chave);
}

/**
 * C6: botão flutuante "Criar novo..." no canto de baixo à direita (o do clone do RD), em todas as páginas do sub-sistema
 * na casca nova. Abre um leque vertical de botões redondos só com ícone (o nome no `title` e no `aria-label`), filtrados por
 * módulo instalado e permissão. Esc fecha o leque e devolve o foco ao botão.
 *
 * Lista de 30/10 B (item 2): o leque fecha também no clique fora dele e sozinho depois de 15 s aberto (além do próprio botão e
 * do Esc). Fechar com o foco dentro do leque (teclado) devolve o foco ao botão, para ele não cair no `body`.
 */
export const PRAZO_LEQUE_MS = 15_000;

export function BotaoFlutuanteCriar({ subsistema }: { subsistema: string | null }) {
  const { t } = useTranslation();
  const s = useSessao();
  const [aberto, setAberto] = useState(false);
  const botao = useRef<HTMLButtonElement>(null);
  const raiz = useRef<HTMLDivElement>(null);
  const criacao = useAbrirCriacao();
  const opcoes = criacoesDoSubsistema(s, subsistema);
  // Fecha o leque; com o foco dentro dele (o item some) ou pelo Esc, o foco volta ao botão.
  const fechar = useCallback((devolver = false) => {
    const dentro = !!raiz.current?.contains(document.activeElement);
    setAberto(false);
    if (devolver || (dentro && document.activeElement !== botao.current)) botao.current?.focus();
  }, []);
  const fecharPeloEsc = useCallback(() => fechar(true), [fechar]);
  useEsc(aberto, fecharPeloEsc);
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      if (!raiz.current?.contains(e.target as Node)) fechar();
    };
    const prazo = setTimeout(() => fechar(), PRAZO_LEQUE_MS);
    document.addEventListener('mousedown', fora);
    return () => {
      clearTimeout(prazo);
      document.removeEventListener('mousedown', fora);
    };
  }, [aberto, fechar]);
  if (!opcoes.length) return null;
  return (
    <>
      <div ref={raiz} className="fixed right-4 bottom-4 z-30 flex flex-col-reverse items-center gap-3 print:hidden sm:right-6 sm:bottom-6" data-botao-flutuante="" data-leque-aberto={aberto ? '' : undefined}>
        <button
          type="button"
          aria-expanded={aberto}
          aria-label={aberto ? t('criacao.fechar') : t('criacao.abrir')}
          title={aberto ? t('criacao.fechar') : t('criacao.abrir')}
          ref={botao}
          onClick={() => setAberto((x) => !x)}
          className="flex size-13 items-center justify-center rounded-full bg-acento text-acento-tinta shadow-lg hover:bg-acento-hover"
          data-abrir-criacao=""
        >
          {aberto ? <X className="size-6" aria-hidden /> : <Plus className="size-6" aria-hidden />}
        </button>
        {aberto && (
          <ul className="flex flex-col items-center gap-2" aria-label={t('criacao.abrir')}>
            {opcoes.map((c) => {
              const Icone = c.criacao.icone;
              const rotulo = t(c.criacao.rotulo);
              return (
                <li key={`${c.slug}.${c.criacao.chave}`}>
                  <button
                    type="button"
                    aria-label={rotulo}
                    title={rotulo}
                    data-criacao={c.criacao.chave}
                    onClick={() => {
                      // Review rcrm3 visual (B2): o item some ao fechar o leque; o foco vai antes ao botão, que o diálogo guarda como
                      // anterior e devolve ao fechar.
                      botao.current?.focus();
                      setAberto(false);
                      criacao.abrir(c);
                    }}
                    className="flex size-11 items-center justify-center rounded-full border border-borda bg-sup-1 text-acento-forte shadow-md hover:bg-sup-3"
                  >
                    <Icone className="size-5" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <DialogoCriacao aberta={criacao.aberta} aoFechar={criacao.fechar} />
    </>
  );
}
